import React, { useRef, useState, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import api from '../../services/api';
import { FileIcon } from './FileIcon';
import { formatFileSize } from './fileIconUtils';

interface FileAttachmentsFormBlockProps {
  /** New files selected for upload */
  newFiles: File[];
  /** Existing attachment IDs to show (with optional fileName for display) */
  existingFiles: Array< { id: number; fileName?: string }>;
  onNewFilesChange: (files: File[]) => void;
  onRemoveExisting: (fileId: number) => void;
  /** In edit mode, API path for GET single file (e.g. `/crm-clients/client/1/files/2`). Used to open/view saved attachments. */
  getViewFileUrl?: (fileId: number) => string;
  /** Optional label above the block */
  label?: string;
  /** Optional hint text */
  hint?: string;
}

export const FileAttachmentsFormBlock: React.FC<FileAttachmentsFormBlockProps> = ({
  newFiles,
  existingFiles,
  onNewFilesChange,
  onRemoveExisting,
  getViewFileUrl,
  label = 'Attachments',
  hint,
}) => {
  const { colors } = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [viewingExistingId, setViewingExistingId] = useState<number | null>(null);

  const mergeNewFiles = useCallback(
    (added: File[]) => {
      if (added.length === 0) return;
      onNewFilesChange([...newFiles, ...added]);
    },
    [newFiles, onNewFilesChange]
  );

  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const added = Array.from(e.target.files || []);
    mergeNewFiles(added);
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files')) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    mergeNewFiles(dropped);
  };

  const removeNew = (index: number) => {
    const next = newFiles.filter((_, i) => i !== index);
    onNewFilesChange(next);
  };

  const openBlobInNewTab = useCallback((blob: Blob) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
    if (win) {
      const revoke = () => window.URL.revokeObjectURL(blobUrl);
      win.addEventListener('beforeunload', revoke, { once: true });
      window.setTimeout(revoke, 120_000);
    } else {
      window.URL.revokeObjectURL(blobUrl);
    }
  }, []);

  const handleViewNewFile = useCallback(
    (file: File) => {
      openBlobInNewTab(file);
    },
    [openBlobInNewTab]
  );

  const handleViewExistingFile = useCallback(
    async (fileId: number) => {
      if (!getViewFileUrl) return;
      setViewingExistingId(fileId);
      try {
        const path = getViewFileUrl(fileId);
        const response = await api.get(path, { responseType: 'blob' });
        const raw = response.data as Blob;
        const ct = (response.headers['content-type'] as string | undefined) || raw.type || 'application/octet-stream';
        const blob = raw.type ? raw : new Blob([raw], { type: ct });
        openBlobInNewTab(blob);
      } catch (err) {
        console.error('View file failed:', err);
      } finally {
        setViewingExistingId(null);
      }
    },
    [getViewFileUrl, openBlobInNewTab]
  );

  const totalCount = newFiles.length + existingFiles.length;

  return (
    <div className="space-y-4">
      {(label || hint) && (
        <div>
          {label && (
            <label className={cn('block text-sm font-medium', colors.text)}>
              {label}
            </label>
          )}
          {hint && (
            <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{hint}</p>
          )}
        </div>
      )}

      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'rounded-xl border-2 border-dashed transition-colors cursor-pointer py-6 px-4 text-center',
          colors.border,
          colors.surfaceHover,
          colors.interactiveHover,
          dragActive && 'ring-2 ring-offset-2 ring-indigo-500/80 dark:ring-indigo-400/80'
        )}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={handleAddFiles}
          className="hidden"
          accept="*/*"
        />
        <div className="flex flex-col items-center gap-2">
          <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', colors.info)}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
          <span className={cn('text-sm font-medium', colors.text)}>
            Add files
          </span>
          <span className={cn('text-xs', colors.textMuted)}>
            Click or drag files here · Multiple files allowed
          </span>
        </div>
      </div>

      {/* New files to upload */}
      {newFiles.length > 0 && (
        <div>
          <p className={cn('text-xs font-medium uppercase tracking-wider mb-2', colors.textMuted)}>
            New uploads ({newFiles.length})
          </p>
          <ul className="space-y-2">
            {newFiles.map((file, index) => (
              <li
                key={`new-${index}-${file.name}`}
                className={cn(
                  'flex items-center gap-2 sm:gap-3 rounded-lg border py-2.5 px-3',
                  colors.border,
                  colors.surface
                )}
              >
                <FileIcon fileName={file.name} size="sm" />
                <button
                  type="button"
                  onClick={() => handleViewNewFile(file)}
                  className={cn('min-w-0 flex-1 text-left rounded-md -m-1 p-1', colors.text, colors.interactiveHover)}
                  title="View"
                >
                  <p className={cn('text-sm font-medium truncate')}>{file.name}</p>
                  <p className={cn('text-xs', colors.textMuted)}>{formatFileSize(file.size)}</p>
                </button>
                <button
                  type="button"
                  onClick={() => handleViewNewFile(file)}
                  className={cn('flex-shrink-0 p-1.5 rounded-md transition-colors', colors.textMuted, colors.interactiveHover)}
                  title="View in new tab"
                  aria-label="View file in new tab"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => removeNew(index)}
                  className={cn('flex-shrink-0 p-1.5 rounded-md transition-colors', colors.error)}
                  title="Remove"
                  aria-label="Remove file from upload list"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {existingFiles.length > 0 && (
        <div>
          <p className={cn('text-xs font-medium uppercase tracking-wider mb-2', colors.textMuted)}>
            Current attachments ({existingFiles.length})
          </p>
          <ul className="space-y-2">
            {existingFiles.map((file) => (
              <li
                key={file.id}
                className={cn(
                  'flex items-center gap-2 sm:gap-3 rounded-lg border py-2.5 px-3',
                  colors.border,
                  colors.surface
                )}
              >
                <FileIcon fileName={file.fileName} size="sm" />
                {getViewFileUrl ? (
                  <button
                    type="button"
                    onClick={() => handleViewExistingFile(file.id)}
                    disabled={viewingExistingId === file.id}
                    className={cn(
                      'min-w-0 flex-1 text-left rounded-md -m-1 p-1 disabled:opacity-60',
                      colors.text,
                      colors.interactiveHover
                    )}
                    title="View"
                  >
                    <p className={cn('text-sm font-medium truncate')}>{file.fileName || 'Unnamed'}</p>
                  </button>
                ) : (
                  <div className="min-w-0 flex-1">
                    <p className={cn('text-sm font-medium truncate', colors.text)}>
                      {file.fileName || 'Unnamed'}
                    </p>
                  </div>
                )}
                {getViewFileUrl && (
                  <button
                    type="button"
                    onClick={() => handleViewExistingFile(file.id)}
                    disabled={viewingExistingId === file.id}
                    className={cn(
                      'flex-shrink-0 p-1.5 rounded-md transition-colors disabled:opacity-60',
                      colors.textMuted,
                      colors.interactiveHover
                    )}
                    title="View in new tab"
                    aria-label="View attachment in new tab"
                  >
                    {viewingExistingId === file.id ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden>
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onRemoveExisting(file.id)}
                  className={cn('flex-shrink-0 p-1.5 rounded-md transition-colors', colors.error)}
                  title="Remove"
                  aria-label="Remove attachment"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {totalCount > 0 && (
        <p className={cn('text-xs', colors.textMuted)}>
          {totalCount} file{totalCount !== 1 ? 's' : ''} will be saved with this record
        </p>
      )}
    </div>
  );
};

export default FileAttachmentsFormBlock;
